import { useEffect, useRef, useState } from 'react'
import { SignaturePadHandle } from '../../components/SignatureCanvas'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@structural-climbing/auth'
import { Button } from '@structural-climbing/ui'
import { usePendingDocuments } from './usePendingDocuments'
import { DocumentSignPanel, SignPanelStep } from './DocumentSignPanel'
import { EmailCodeForm } from '../account/EmailCodeForm'
import { EMPTY_GUARDIAN, GuardianFields, guardianComplete } from './GuardianFields'

type Step = SignPanelStep

interface Props {
  /** The gym the member is looking at — the documents are scoped to it. */
  gymSlug?: string | null
}

export function LegalDocumentGate({ gymSlug }: Props) {
  const { pendingDocs, privacyNotice, emailVerification, guardian, loading, refetch } =
    usePendingDocuments(gymSlug)
  const gymQuery = gymSlug ? `?gym=${encodeURIComponent(gymSlug)}` : ''
  const { getAccessTokenSilently } = useAuth()
  const { t } = useTranslation()

  const [index, setIndex] = useState(0)
  const [step, setStep] = useState<Step>('view')
  const [docUrl, setDocUrl] = useState<string | null>(null)
  const [docLoading, setDocLoading] = useState(false)
  const [signing, setSigning] = useState(false)
  const [canvasEmpty, setCanvasEmpty] = useState(true)
  // The statements ticked on the document on screen. Emptied on every move to
  // another document: a box is never ticked for the signer.
  const [ticked, setTicked] = useState<string[]>([])
  const [error, setError] = useState<string | null>(null)
  // A member asked to sign something they already signed is told why first.
  const [resignExplained, setResignExplained] = useState(false)
  // A member under 18 does not sign. Their parent or guardian does, and says
  // who they are first: named, declared, and at an address they proved.
  const [guardianValue, setGuardianValue] = useState(EMPTY_GUARDIAN)
  const [guardianStep, setGuardianStep] = useState<'details' | 'code' | 'done'>('details')
  const [guardianCode, setGuardianCode] = useState<{ email: string; id: string } | null>(null)

  const sigRef = useRef<SignaturePadHandle | null>(null)
  const objectUrlRef = useRef<string | null>(null)

  const currentDoc = pendingDocs[index]

  // Fetch and display the document whenever currentDoc changes
  useEffect(() => {
    if (!currentDoc) return
    let cancelled = false
    setDocLoading(true)
    setTicked([])
    setError(null)

    // Revoke any previous object URL
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current)
      objectUrlRef.current = null
      setDocUrl(null)
    }

    async function load() {
      try {
        const token = await getAccessTokenSilently()
        if (cancelled) return
        const res = await fetch(
          `${import.meta.env.VITE_API_URL as string}/my-gym-document/${currentDoc.id}${gymQuery}`,
          { headers: { Authorization: `Bearer ${token}` } },
        )
        if (cancelled || !res.ok) return
        const blob = await res.blob()
        if (cancelled) return
        const url = URL.createObjectURL(blob)
        objectUrlRef.current = url
        setDocUrl(url)
      } catch {
        // silently fail — user sees an error state in the viewer
      } finally {
        if (!cancelled) setDocLoading(false)
      }
    }

    void load()
    return () => {
      cancelled = true
    }
    // Keyed on the version as well as the id. A gym updating a document keeps
    // its id and bumps the version, so on id alone this effect never re-ran and
    // the member kept reading — and signing against — the copy already on
    // screen, while the server recorded them as having accepted the new one.
    // The child is part of the key too: a document signed per child comes back
    // once for each, and moving from one child to the next must reset the view
    // rather than leave the previous child's copy half-scrolled on screen.
    // getAccessTokenSilently is stable per Auth0 SDK contract
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDoc?.id, currentDoc?.version, currentDoc?.child?.id, gymQuery])

  // Once everything is signed the explanation is owed again next time.
  useEffect(() => {
    if (pendingDocs.length === 0) {
      setResignExplained(false)
      setGuardianStep('details')
    }
  }, [pendingDocs.length])

  // Whoever signed last time is filled in, so they are not asked to type it
  // again. The declaration is not: that box is ticked by the person signing.
  useEffect(() => {
    if (!guardian) return
    setGuardianValue((current) =>
      current.firstName || current.lastName || current.email
        ? current
        : {
            firstName: guardian.first_name ?? '',
            lastName: guardian.last_name ?? '',
            email: guardian.email ?? '',
            phone: '',
            declared: false,
          },
    )
  }, [guardian])

  // Clean up object URL on unmount
  useEffect(() => {
    return () => {
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current)
    }
  }, [])

  if (loading) return null
  if (!currentDoc) return null

  const isPdf = currentDoc.r2_key.toLowerCase().endsWith('.pdf')
  const total = pendingDocs.length

  const ownNameChanged = pendingDocs.some((d) => d.reason === 'name_changed')
  // Children whose own name changed, each once however many documents they have.
  const renamedChildren = [
    ...new Map(
      pendingDocs
        .filter((d) => d.reason === 'child_name_changed' && d.child)
        .map((d) => [
          d.child!.id,
          [d.child!.first_name, d.child!.last_name].filter(Boolean).join(' '),
        ]),
    ).values(),
  ]
  const cameOfAge = pendingDocs.some((d) => d.reason === 'came_of_age')
  const showResignIntro =
    !resignExplained && (ownNameChanged || renamedChildren.length > 0 || cameOfAge)
  const guardianEmail = guardianValue.email.trim().toLowerCase()
  const codeGymSlug = guardian?.gym_slug ?? gymSlug ?? null
  const needsGuardian = currentDoc.guardian_required === true && guardianStep !== 'done'

  function confirmGuardian() {
    if (guardian?.email_required && codeGymSlug && guardianCode?.email !== guardianEmail)
      setGuardianStep('code')
    else setGuardianStep('done')
  }
  // Asked before the first document, so nobody draws a signature the Worker is
  // about to refuse.
  const needsEmailCode =
    emailVerification?.required === true && !emailVerification.verified && !!emailVerification.email

  function advance() {
    sigRef.current?.clear()
    setCanvasEmpty(true)
    setStep('view')

    const next = index + 1
    if (next < total) {
      setIndex(next)
    } else {
      setIndex(0)
      void emailSignedCopy()
      refetch()
    }
  }

  // Asked once, after the last document, so the member gets one email with
  // everything they signed rather than one per signature. Nothing waits on it:
  // a copy that is not sent now is sent later by the Worker.
  async function emailSignedCopy() {
    try {
      const token = await getAccessTokenSilently()
      await fetch(`${import.meta.env.VITE_API_URL as string}/signed-documents/email${gymQuery}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      })
    } catch {
      // The Worker sends what was missed.
    }
  }

  async function handleSign() {
    if (!sigRef.current || sigRef.current.isEmpty()) return
    setSigning(true)
    setError(null)
    try {
      const token = await getAccessTokenSilently()
      const dataUrl = sigRef.current.toDataURL('image/png')
      const blob = await fetch(dataUrl).then((r) => r.blob())
      const body = new FormData()
      body.append('file', blob, `${currentDoc.id}.png`)
      body.append('document_id', currentDoc.id)
      if (currentDoc.child) body.append('child_id', currentDoc.child.id)
      // Always sent, even empty: it is how the Worker knows this build showed
      // the statements, and it refuses a signature that does not say so.
      body.append('ticked', JSON.stringify(ticked))
      if (currentDoc.guardian_required) {
        body.append('guardian_first_name', guardianValue.firstName.trim())
        body.append('guardian_last_name', guardianValue.lastName.trim())
        body.append('guardian_email', guardianEmail)
        body.append('guardian_declaration', String(guardianValue.declared))
        if (guardianCode?.email === guardianEmail) body.append('verification_id', guardianCode.id)
      }
      const res = await fetch(
        `${import.meta.env.VITE_API_URL as string}/sign-document${gymQuery}`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body,
        },
      )
      if (!res.ok) {
        // This used to fail in silence: the button stopped spinning and the
        // member was left looking at a signature that had not been saved.
        const data = (await res.json().catch(() => ({}))) as { code?: string }
        // The gym turned the requirement on, or the address changed, since
        // this screen loaded. Reading again brings up the code step.
        if (data.code === 'EMAIL_NOT_VERIFIED') {
          // For a guardian the confirmation is only good for a while. Asking
          // for a fresh code is all that is needed.
          if (currentDoc.guardian_required) {
            setGuardianCode(null)
            setGuardianStep('details')
          } else refetch()
          return
        }
        setError(
          t(data.code === 'UPDATE_REQUIRED' ? 'legal.refreshToContinue' : 'legal.signFailed'),
        )
        return
      }
      advance()
    } catch {
      setError(t('legal.signFailed'))
    } finally {
      setSigning(false)
    }
  }

  return (
    <div className="fixed inset-0 z-[var(--z-gate)] flex items-center justify-center bg-background/95 p-3 backdrop-blur-sm sm:p-4">
      {/* max-h-full and overflow-hidden are load-bearing: the overlay centres
          this card, so a card that outgrows it gets clipped at both ends and
          the sign button disappears off the bottom. Bounded here, the panel
          inside shrinks the document instead. */}
      {needsEmailCode ? (
        <div className="flex max-h-full w-full max-w-md flex-col gap-4 overflow-y-auto rounded-2xl border border-divider bg-content1 p-6 pb-[calc(1.5rem+var(--safe-b))] shadow-large">
          <h2 className="text-lg font-semibold text-foreground">
            {t('emailCode.beforeSigningTitle')}
          </h2>
          <p className="text-sm text-foreground">{t('emailCode.beforeSigningBody')}</p>
          <EmailCodeForm email={emailVerification.email!} onVerified={() => refetch()} />
        </div>
      ) : needsGuardian ? (
        <div className="flex max-h-full w-full max-w-md flex-col gap-4 overflow-y-auto rounded-2xl border border-divider bg-content1 p-6 pb-[calc(1.5rem+var(--safe-b))] shadow-large">
          <h2 className="text-lg font-semibold text-foreground">{t('legal.guardianStepTitle')}</h2>
          {guardianStep === 'code' && codeGymSlug ? (
            <>
              <p className="text-sm text-foreground">{t('legal.guardianEmailHint')}</p>
              <EmailCodeForm
                email={guardianEmail}
                guest={{ gymSlug: codeGymSlug, email: guardianEmail }}
                onVerified={(result) => {
                  setGuardianCode({ email: guardianEmail, id: result.verificationId })
                  setGuardianStep('done')
                }}
              />
              <Button variant="light" size="sm" onClick={() => setGuardianStep('details')}>
                {t('emailCode.changeEmail')}
              </Button>
            </>
          ) : (
            <>
              <p className="text-sm text-foreground">{t('legal.guardianStepBody')}</p>
              <GuardianFields
                value={guardianValue}
                onChange={setGuardianValue}
                minorName={guardian?.member_name ?? ''}
                gymName={guardian?.gym_name ?? ''}
                withEmail
                memberEmail={guardian?.member_email}
              />
              <Button
                color="primary"
                isDisabled={
                  !guardianComplete(guardianValue, {
                    needsEmail: true,
                    memberEmail: guardian?.member_email,
                  })
                }
                onClick={confirmGuardian}
              >
                {t('legal.guardianContinue')}
              </Button>
            </>
          )}
        </div>
      ) : showResignIntro ? (
        <div className="flex max-h-full w-full max-w-md flex-col gap-4 overflow-y-auto rounded-2xl border border-divider bg-content1 p-6 pb-[calc(1.5rem+var(--safe-b))] shadow-large">
          <h2 className="text-lg font-semibold text-foreground">
            {cameOfAge && !ownNameChanged && renamedChildren.length === 0
              ? t('legal.resignCameOfAgeTitle')
              : t('legal.resignTitle')}
          </h2>
          {cameOfAge && <p className="text-sm text-foreground/70">{t('legal.resignCameOfAge')}</p>}
          {ownNameChanged && (
            <p className="text-sm text-foreground/70">{t('legal.resignNameChanged')}</p>
          )}
          {renamedChildren.length > 0 && (
            <p className="text-sm text-foreground/70">
              {t('legal.resignChildNameChanged', { names: renamedChildren.join(', ') })}
            </p>
          )}
          <Button color="primary" onClick={() => setResignExplained(true)}>
            {t('legal.resignContinue')}
          </Button>
        </div>
      ) : (
        <div className="flex h-full max-h-full w-full max-w-3xl flex-col gap-3 overflow-hidden rounded-2xl border border-divider bg-content1 p-4 pb-[calc(1rem+var(--safe-b))] shadow-large sm:max-h-[90dvh] sm:gap-4 sm:p-6">
          <div className="shrink-0">
            <p className="text-xs font-medium uppercase tracking-widest text-foreground/40">
              {t('legal.documentOf', { current: index + 1, total })}
            </p>
            <h2 className="text-lg font-semibold text-foreground">{currentDoc.title}</h2>
            {currentDoc.guardian_required && (
              <p className="text-sm font-medium text-primary">
                {t('legal.signingAsGuardian', { name: guardian?.member_name ?? '' })}
              </p>
            )}
            {currentDoc.child && (
              <p className="text-sm font-medium text-primary">
                {t('legal.signingForChild', {
                  name: [currentDoc.child.first_name, currentDoc.child.last_name]
                    .filter(Boolean)
                    .join(' '),
                })}
              </p>
            )}
            <p className="text-xs text-foreground/40">v{currentDoc.version}</p>
          </div>

          <DocumentSignPanel
            step={step}
            docTitle={currentDoc.title}
            docUrl={docUrl}
            docIsPdf={isPdf}
            docLoading={docLoading}
            error={error}
            statements={currentDoc.statements}
            ticked={ticked}
            onTickedChange={setTicked}
            privacyNoticeUrl={
              privacyNotice
                ? `${import.meta.env.VITE_API_URL as string}${privacyNotice.path}`
                : null
            }
            busy={signing}
            signLabel={index + 1 < total ? t('legal.signAndContinue') : t('legal.signAndEnter')}
            canvasEmpty={canvasEmpty}
            sigRef={sigRef}
            onCanvasEnd={() => setCanvasEmpty(sigRef.current?.isEmpty() ?? true)}
            onClear={() => {
              sigRef.current?.clear()
              setCanvasEmpty(true)
            }}
            onProceed={() => setStep('sign')}
            onBack={() => setStep('view')}
            onSign={() => void handleSign()}
          />
        </div>
      )}
    </div>
  )
}
